import React from 'react';
import { useParams, Link, useNavigate } from 'react-router-dom';
import { toast } from 'sonner';
import {
  ChevronLeft, ChevronRight, MapPin, Gauge, Zap, Calendar, ShieldCheck, Cog,
  MessageCircle, Send, ArrowLeft, Loader2, Car, User, Edit3, Check, X as XIcon, Navigation,
  CheckCircle2,
} from 'lucide-react';
import { getVehicle, listInquiries, createInquiry } from '../lib/vehicles';
import { updateVehiclePrice, getSavedEmail } from '../lib/watchlist';
import { markVehicleSold } from '../lib/profile';
import HeartButton from '../components/HeartButton';
import RoutePlannerModal from '../components/RoutePlannerModal';
import VerifiedBadge from '../components/VerifiedBadge';

function fmtMoney(n) { return `$${(n || 0).toLocaleString()}`; }
function fmtTime(iso) {
  try { return new Date(iso).toLocaleString('en-CA', { month: 'short', day: 'numeric', hour: '2-digit', minute: '2-digit' }); }
  catch { return iso; }
}

export default function VehicleDetail() {
  const { id } = useParams();
  const navigate = useNavigate();
  const [vehicle, setVehicle] = React.useState(null);
  const [loading, setLoading] = React.useState(true);
  const [error, setError] = React.useState(null);
  const [idx, setIdx] = React.useState(0);
  const [inquiries, setInquiries] = React.useState([]);

  const [buyerEmail, setBuyerEmail] = React.useState(() => localStorage.getItem('am_billing_email') || '');
  const [msg, setMsg] = React.useState('');
  const [sending, setSending] = React.useState(false);

  const inquiryRef = React.useRef(null);
  const scrollToInquiry = () => inquiryRef.current?.scrollIntoView({ behavior: 'smooth', block: 'start' });
  const [showRoutePlanner, setShowRoutePlanner] = React.useState(false);
  const [markingSold, setMarkingSold] = React.useState(false);

  const doMarkSold = async () => {
    if (!window.confirm(`Mark "${vehicle.title}" as SOLD?\nBuyers can no longer contact you and the listing will be hidden from the marketplace.`)) return;
    try {
      setMarkingSold(true);
      const updated = await markVehicleSold(vehicle.id, getSavedEmail());
      setVehicle(updated);
      toast.success('Marked as SOLD — added to your sales history');
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Failed to mark sold');
    } finally {
      setMarkingSold(false);
    }
  };

  // Seller-only price editor
  const [editingPrice, setEditingPrice] = React.useState(false);
  const [newPrice, setNewPrice] = React.useState('');
  const [savingPrice, setSavingPrice] = React.useState(false);
  const isSeller = !!vehicle?.is_owner;

  const savePrice = async () => {
    const n = parseInt(String(newPrice).replace(/[^\d]/g, ''), 10);
    if (!n || n <= 0) return toast.error('Enter a valid price');
    try {
      setSavingPrice(true);
      const updated = await updateVehiclePrice(vehicle.id, getSavedEmail(), n);
      setVehicle(updated);
      setEditingPrice(false);
      if (n < vehicle.price) {
        toast.success(`Price dropped from $${vehicle.price.toLocaleString()} to $${n.toLocaleString()} — watchers notified`);
      } else if (n > vehicle.price) {
        toast.success('Price updated');
      } else {
        toast.info('Price unchanged');
      }
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Update failed');
    } finally {
      setSavingPrice(false);
    }
  };

  React.useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        setLoading(true);
        const [v, inqs] = await Promise.all([getVehicle(id), listInquiries(id)]);
        if (cancelled) return;
        setVehicle(v);
        setInquiries(inqs);
      } catch (e) {
        if (cancelled) return;
        setError(e?.response?.status === 404 ? 'notfound' : (e?.message || 'error'));
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, [id]);

  const photos = (vehicle?.photos?.length ? vehicle.photos : []).filter(Boolean);
  const activePhoto = photos[idx];

  const send = async () => {
    if (!msg.trim()) return toast.error('Type a message');
    if (!/\S+@\S+\.\S+/.test(buyerEmail)) return toast.error('Enter a valid email');
    try {
      setSending(true);
      const created = await createInquiry({ vehicle_id: id, from_email: buyerEmail, message: msg.trim() });
      setInquiries((prev) => [created, ...prev]);
      setMsg('');
      localStorage.setItem('am_billing_email', buyerEmail);
      toast.success('Inquiry sent — seller will reply by email');
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Send failed');
    } finally {
      setSending(false);
    }
  };

  if (loading) {
    return (
      <div className="min-h-[60vh] grid place-items-center" data-testid="vehicle-loading">
        <Loader2 className="w-8 h-8 text-[#ff2a2a] animate-spin" />
      </div>
    );
  }

  if (error === 'notfound' || !vehicle) {
    return (
      <div className="mx-auto max-w-3xl px-6 lg:px-10 py-16 text-center" data-testid="vehicle-notfound">
        <Car className="w-12 h-12 text-zinc-600 mx-auto" />
        <h1 className="mt-4 font-display text-4xl uppercase tracking-tight">Listing not found</h1>
        <p className="mt-2 text-zinc-400 text-sm">This vehicle may have sold or been removed.</p>
        <Link to="/marketplace" className="mt-6 inline-block bg-[#ff2a2a] hover:bg-[#e01f1f] text-white text-sm font-semibold px-6 py-3 transition-colors">
          Back to Marketplace
        </Link>
      </div>
    );
  }

  const specs = [
    ['Year', vehicle.year, Calendar],
    ['Make', vehicle.make, Car],
    ['Model', vehicle.model, Car],
    ['Mileage', `${(vehicle.mileage || 0).toLocaleString()} km`, Gauge],
    vehicle.hp ? ['Power', `${vehicle.hp} HP`, Zap] : null,
    vehicle.transmission ? ['Transmission', vehicle.transmission, Cog] : null,
    vehicle.drivetrain ? ['Drivetrain', vehicle.drivetrain, Cog] : null,
    vehicle.condition ? ['Condition', vehicle.condition, ShieldCheck] : null,
    ['Location', vehicle.location, MapPin],
    vehicle.vin ? ['VIN', vehicle.vin, ShieldCheck] : null,
  ].filter(Boolean);

  return (
    <div data-testid="page-vehicle-detail">
      <div className="mx-auto max-w-7xl px-6 lg:px-10 pt-6">
        <button onClick={() => navigate(-1)} className="text-xs font-mono uppercase tracking-widest text-zinc-500 hover:text-white inline-flex items-center gap-1.5 transition-colors" data-testid="vehicle-back">
          <ArrowLeft className="w-3.5 h-3.5" /> Back
        </button>
      </div>

      <div className="mx-auto max-w-7xl px-6 lg:px-10 py-8 grid lg:grid-cols-12 gap-8">
        {/* Left: photo carousel */}
        <div className="lg:col-span-8">
          <div className="relative am-card overflow-hidden aspect-[16/10]" data-testid="vehicle-carousel">
            {activePhoto ? (
              <img src={activePhoto} alt={vehicle.title} className={`w-full h-full object-cover ${vehicle.status === 'sold' ? 'grayscale opacity-60' : ''}`} />
            ) : (
              <div className="w-full h-full grid place-items-center text-zinc-600">
                <div className="text-center">
                  <Car className="w-12 h-12 mx-auto" />
                  <div className="mt-2 text-xs font-mono uppercase tracking-widest">No photos provided</div>
                </div>
              </div>
            )}
            {vehicle.status === 'sold' && (
              <div className="absolute inset-0 grid place-items-center pointer-events-none" data-testid="sold-watermark">
                <div className="rotate-[-8deg] border-4 border-emerald-400 text-emerald-400 font-display text-5xl sm:text-7xl uppercase tracking-tighter px-8 py-3 bg-black/60 backdrop-blur-sm">
                  SOLD
                </div>
              </div>
            )}
            {photos.length > 1 && (
              <>
                <button
                  onClick={() => setIdx((i) => (i - 1 + photos.length) % photos.length)}
                  className="absolute left-3 top-1/2 -translate-y-1/2 w-10 h-10 grid place-items-center bg-black/70 hover:bg-[#ff2a2a] text-white transition-colors"
                  data-testid="carousel-prev"
                  aria-label="Previous photo"
                >
                  <ChevronLeft className="w-5 h-5" />
                </button>
                <button
                  onClick={() => setIdx((i) => (i + 1) % photos.length)}
                  className="absolute right-3 top-1/2 -translate-y-1/2 w-10 h-10 grid place-items-center bg-black/70 hover:bg-[#ff2a2a] text-white transition-colors"
                  data-testid="carousel-next"
                  aria-label="Next photo"
                >
                  <ChevronRight className="w-5 h-5" />
                </button>
                <div className="absolute bottom-3 right-3 font-mono text-[10px] uppercase tracking-widest text-white bg-black/70 px-2 py-1">
                  {idx + 1} / {photos.length}
                </div>
              </>
            )}
            {vehicle.tag && (
              <div className="absolute top-3 left-3 am-pill bg-black/70 backdrop-blur-md">{vehicle.tag}</div>
            )}
          </div>

          {photos.length > 1 && (
            <div className="mt-3 grid grid-cols-6 gap-2" data-testid="carousel-thumbs">
              {photos.map((p, i) => (
                <button
                  key={i}
                  onClick={() => setIdx(i)}
                  className={`aspect-[4/3] overflow-hidden hairline transition-colors ${idx === i ? 'border-[#ff2a2a]' : ''}`}
                  data-testid={`thumb-${i}`}
                >
                  <img src={p} alt={`thumb-${i}`} className="w-full h-full object-cover" />
                </button>
              ))}
            </div>
          )}

          {/* Description */}
          {vehicle.description && (
            <div className="mt-8 am-card p-6" data-testid="vehicle-description">
              <div className="font-mono text-xs uppercase tracking-widest text-zinc-500 mb-3">About this vehicle</div>
              <p className="text-sm text-zinc-200 leading-relaxed whitespace-pre-wrap">{vehicle.description}</p>
            </div>
          )}

          {/* Spec table */}
          <div className="mt-8 am-card overflow-hidden" data-testid="vehicle-specs">
            <div className="p-4 hairline-b font-mono text-xs uppercase tracking-widest text-zinc-500">Specifications</div>
            <table className="w-full text-sm">
              <tbody>
                {specs.map(([label, value, Icon], i) => (
                  <tr key={i} className="hairline-b last:border-b-0" data-testid={`spec-${label.toLowerCase()}`}>
                    <td className="p-4 text-zinc-400 flex items-center gap-2 w-1/3">
                      <Icon className="w-4 h-4 text-[#ff2a2a]" /> {label}
                    </td>
                    <td className="p-4 text-white font-medium">{value}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          {/* Inline free inquiry */}
          <div ref={inquiryRef} id="inquiry" className="mt-8 am-card p-6" data-testid="vehicle-inquiry">
            <div className="flex items-center gap-2 mb-1">
              <MessageCircle className="w-4 h-4 text-[#ff2a2a]" />
              <div className="font-display text-2xl uppercase tracking-tight">Ask the seller</div>
              <span className="ml-2 am-pill !py-0.5 !px-2 !text-[9px]">Free</span>
            </div>
            <p className="text-xs text-zinc-500">Inquiries are always free. Seller replies by email — contact stays private (PIPEDA).</p>

            <div className="mt-5 space-y-3">
              <input
                type="email"
                value={buyerEmail}
                onChange={(e) => setBuyerEmail(e.target.value)}
                placeholder="Your email"
                className="w-full bg-[#0d0d0d] hairline px-3 py-2.5 text-sm placeholder:text-zinc-600 focus:outline-none focus:border-[#ff2a2a]"
                data-testid="inquiry-email"
              />
              <div className="flex gap-2">
                <input
                  value={msg}
                  onChange={(e) => setMsg(e.target.value)}
                  onKeyDown={(e) => e.key === 'Enter' && send()}
                  placeholder="Is service history available? Delivery to Toronto?"
                  className="flex-1 bg-[#0d0d0d] hairline px-3 py-2.5 text-sm placeholder:text-zinc-600 focus:outline-none focus:border-[#ff2a2a]"
                  data-testid="inquiry-msg"
                />
                <button
                  onClick={send}
                  disabled={sending}
                  className="bg-[#ff2a2a] hover:bg-[#e01f1f] text-white text-sm font-semibold px-4 transition-colors inline-flex items-center gap-1 disabled:opacity-60"
                  data-testid="inquiry-submit"
                >
                  {sending ? <Loader2 className="w-4 h-4 animate-spin" /> : <Send className="w-4 h-4" />}
                </button>
              </div>
            </div>

            {inquiries.length > 0 && (
              <div className="mt-6 space-y-3" data-testid="inquiry-thread">
                <div className="font-mono text-xs uppercase tracking-widest text-zinc-500">Recent inquiries ({inquiries.length})</div>
                {inquiries.slice(0, 5).map((q) => (
                  <div key={q.id} className="hairline p-3 text-sm">
                    <div className="flex items-center justify-between gap-2">
                      <div className="flex items-center gap-1.5 text-zinc-400 text-xs font-mono uppercase tracking-widest">
                        <User className="w-3 h-3" /> {q.from_label}
                      </div>
                      <div className="text-[10px] font-mono uppercase tracking-widest text-zinc-600">{fmtTime(q.created_at)}</div>
                    </div>
                    <div className="mt-1.5 text-zinc-200">{q.message}</div>
                  </div>
                ))}
              </div>
            )}
          </div>
        </div>

        {/* Right: sticky buy panel */}
        <aside className="lg:col-span-4">
          <div className="lg:sticky lg:top-24 space-y-4">
            <div className="am-card p-6" data-testid="buy-panel">
              <div className="font-mono text-[10px] uppercase tracking-widest text-zinc-500">{vehicle.make} · {vehicle.year}</div>
              <h1 className="mt-1 font-display text-3xl uppercase tracking-tight leading-tight">{vehicle.title}</h1>
              <div className="mt-4 flex items-center justify-between gap-3">
                {editingPrice ? (
                  <div className="flex items-center gap-2 flex-1">
                    <span className="font-display text-2xl text-[#ff2a2a]">$</span>
                    <input
                      type="number"
                      autoFocus
                      value={newPrice}
                      onChange={(e) => setNewPrice(e.target.value)}
                      className="flex-1 bg-[#0d0d0d] hairline px-2 py-1 font-display text-2xl text-white focus:outline-none focus:border-[#ff2a2a]"
                      data-testid="price-edit-input"
                    />
                    <button
                      onClick={savePrice}
                      disabled={savingPrice}
                      className="w-8 h-8 grid place-items-center bg-[#ff2a2a] hover:bg-[#e01f1f] text-white transition-colors"
                      data-testid="price-edit-save"
                    >
                      {savingPrice ? <Loader2 className="w-4 h-4 animate-spin" /> : <Check className="w-4 h-4" />}
                    </button>
                    <button
                      onClick={() => setEditingPrice(false)}
                      className="w-8 h-8 grid place-items-center hairline text-zinc-400 hover:text-white transition-colors"
                      data-testid="price-edit-cancel"
                    >
                      <XIcon className="w-4 h-4" />
                    </button>
                  </div>
                ) : (
                  <>
                    <div className="font-display text-4xl text-[#ff2a2a]" data-testid="vehicle-price">{fmtMoney(vehicle.price)}</div>
                    <HeartButton vehicleId={vehicle.id} dataTestId="detail-heart" />
                  </>
                )}
              </div>
              {isSeller && !editingPrice && (
                <div className="mt-2 flex items-center gap-3">
                  <button
                    onClick={() => { setEditingPrice(true); setNewPrice(String(vehicle.price)); }}
                    className="text-[10px] font-mono uppercase tracking-widest text-zinc-400 hover:text-[#ff2a2a] transition-colors inline-flex items-center gap-1"
                    data-testid="edit-price-btn"
                  >
                    <Edit3 className="w-3 h-3" /> Edit price
                  </button>
                  {vehicle.status !== 'sold' && (
                    <button
                      onClick={doMarkSold}
                      disabled={markingSold}
                      className="text-[10px] font-mono uppercase tracking-widest text-emerald-400 hover:text-emerald-300 transition-colors inline-flex items-center gap-1 disabled:opacity-60"
                      data-testid="mark-sold-btn"
                    >
                      {markingSold ? <Loader2 className="w-3 h-3 animate-spin" /> : <CheckCircle2 className="w-3 h-3" />} Mark as SOLD
                    </button>
                  )}
                </div>
              )}
              <div className="mt-1 text-xs text-zinc-500">Commission-based · No listing fees</div>

              <div className="mt-5 grid grid-cols-3 gap-2 text-[10px] font-mono uppercase tracking-widest text-zinc-400">
                <div className="hairline p-2 text-center"><Gauge className="w-3 h-3 mx-auto text-[#ff2a2a] mb-1" />{(vehicle.mileage/1000).toFixed(0)}k km</div>
                {vehicle.hp && <div className="hairline p-2 text-center"><Zap className="w-3 h-3 mx-auto text-[#ff2a2a] mb-1" />{vehicle.hp} HP</div>}
                <div className="hairline p-2 text-center"><MapPin className="w-3 h-3 mx-auto text-[#ff2a2a] mb-1" />{vehicle.location.split(',')[0]}</div>
              </div>

              <button
                onClick={scrollToInquiry}
                disabled={vehicle.status === 'sold'}
                className={`mt-6 w-full text-sm font-semibold py-3 inline-flex items-center justify-center gap-2 transition-colors ${
                  vehicle.status === 'sold'
                    ? 'bg-zinc-800 text-zinc-500 cursor-not-allowed'
                    : 'bg-[#ff2a2a] hover:bg-[#e01f1f] text-white'
                }`}
                data-testid="contact-seller"
              >
                {vehicle.status === 'sold' ? (
                  <><CheckCircle2 className="w-4 h-4" /> This vehicle is sold</>
                ) : (
                  <><MessageCircle className="w-4 h-4" /> Contact seller</>
                )}
              </button>

              <div className="mt-3 text-[10px] font-mono uppercase tracking-widest text-emerald-400 flex items-center gap-1.5">
                <ShieldCheck className="w-3.5 h-3.5" /> Free inquiry · Direct with owner
              </div>

              <div className="mt-6 hairline-t pt-5 text-xs text-zinc-400 space-y-1">
                <div className="flex justify-between items-center gap-2">
                  <span>Seller</span>
                  <span className="text-white inline-flex items-center gap-1.5">
                    {vehicle.seller_name || 'Private seller'}
                    {vehicle.seller_verified && <VerifiedBadge compact />}
                  </span>
                </div>
                <div className="flex justify-between"><span>Listed</span><span className="text-white">{fmtTime(vehicle.created_at)}</span></div>
              </div>
            </div>

            <Link to="/maps" className="am-card p-4 flex items-start gap-3 hover:border-[#ff2a2a] transition-colors" data-testid="maps-link-card">
              <MapPin className="w-5 h-5 text-[#ff2a2a] shrink-0 mt-0.5" />
              <div>
                <div className="text-sm text-white font-semibold">Plan your pickup</div>
                <div className="text-xs text-zinc-400 mt-0.5">Gas, EV chargers &amp; service stops along the route</div>
              </div>
            </Link>

            <button
              onClick={() => setShowRoutePlanner(true)}
              className="am-card p-4 flex items-start gap-3 hover:border-[#ff2a2a] transition-colors w-full text-left"
              data-testid="open-route-planner"
            >
              <Navigation className="w-5 h-5 text-[#ff2a2a] shrink-0 mt-0.5" />
              <div>
                <div className="text-sm text-white font-semibold">One-tap route planner</div>
                <div className="text-xs text-zinc-400 mt-0.5">Directions to {vehicle.location} with fuel/EV/service stops</div>
              </div>
            </button>
          </div>
        </aside>
      </div>

      <RoutePlannerModal
        open={showRoutePlanner}
        onClose={() => setShowRoutePlanner(false)}
        destination={vehicle.location}
      />
    </div>
  );
}
